import { defineMessages, IntlShape } from 'react-intl';
import { readM24Error } from '../music24/utils/parseM24LimitError';

/**
 * Every team-related refusal the UI can turn into a sentence. `M24_LIMIT_SEATS` is here too so
 * this stays a complete map of `error.data.m24Limit.code` — the global upgrade prompt already
 * shows its own copy for that one (`dispatchM24LimitPrompt`, fired by every tRPC call), so no
 * caller in this module reads this entry for `M24_LIMIT_SEATS`.
 */
const messages = defineMessages({
  M24_LIMIT_SEATS: {
    id: 'app.sml.team.error.seats',
    defaultMessage: 'Your plan has no free seats. Upgrade to invite more people.',
  },
  M24_TEAM_OWNER_ONLY: {
    id: 'app.sml.team.error.ownerOnly',
    defaultMessage: 'Only the workspace owner can do this.',
  },
  M24_TEAM_INVITE_INVALID: {
    id: 'app.sml.team.error.inviteInvalid',
    defaultMessage: 'This invite was used, cancelled or has expired. Ask for a new one.',
  },
  M24_TEAM_EMAIL_MISMATCH: {
    id: 'app.sml.team.error.emailMismatch',
    defaultMessage: 'This invite is for a different email address. Sign in with the invited email.',
  },
  M24_TEAM_ALREADY_MEMBER: {
    id: 'app.sml.team.error.alreadyMember',
    defaultMessage: 'This person is already in the team.',
  },
  M24_TEAM_OWNER_CANNOT_LEAVE: {
    id: 'app.sml.team.error.ownerCannotLeave',
    defaultMessage: 'The owner cannot leave the team.',
  },
  M24_TEAM_HOME_CANNOT_LEAVE: {
    id: 'app.sml.team.error.homeCannotLeave',
    defaultMessage: 'This is your own workspace. You cannot leave it.',
  },
  M24_TEAM_NOT_A_MEMBER: {
    id: 'app.sml.team.error.notAMember',
    defaultMessage: 'This person is not a member of the team.',
  },
  busy: {
    id: 'app.sml.team.error.busy',
    defaultMessage: 'The workspace is busy. Try again.',
  },
  generic: {
    id: 'app.sml.team.error.generic',
    defaultMessage: 'Something went wrong. Try again.',
  },
});

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** The tRPC error code (`FORBIDDEN`, `CONFLICT`, …), as a tRPC client error carries it. */
function readTRPCCode(error: unknown): string | null {
  if (!isRecord(error)) {
    return null;
  }
  const data = isRecord(error.data) ? error.data : null;
  return typeof data?.code === 'string' ? data.code : null;
}

/**
 * A team-router error, as a sentence to show the person. A busy workspace lock (`CONFLICT`, no
 * `m24Limit` code — `withTeamLocks` in `music24-api`) gets its own sentence; anything else
 * unrecognised falls back to a generic one.
 */
export function teamErrorMessage(intl: IntlShape, error: unknown): string {
  const code = readM24Error(error)?.code ?? '';
  if (code in messages) {
    return intl.formatMessage(messages[code as keyof typeof messages]);
  }
  if (readTRPCCode(error) === 'CONFLICT') {
    return intl.formatMessage(messages.busy);
  }
  return intl.formatMessage(messages.generic);
}
